// AuditListPage — recent audits for a single Bubble app.
// URL: /apps/:appId/audits
// Fetches GET /api/v1/audits?app_id=<appId>.

import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ScanLine,
} from "lucide-react";
import { fetchAudits, type Audit } from "../api";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";
import { EmptyState } from "@avagency/frontend/components/shell/EmptyState";
import { Footer } from "../components/Footer";

interface StatusConfig {
  icon: React.ReactNode;
  label: string;
  color: string;
  bg: string;
  border: string;
}

function getStatusConfig(status: string): StatusConfig {
  switch (status) {
    case "complete":
      return {
        icon: <CheckCircle2 className="h-4 w-4" />,
        label: "Complete",
        color: "hsl(145 65% 68%)",
        bg: "hsl(145 65% 68% / 0.08)",
        border: "hsl(145 65% 68% / 0.22)",
      };
    case "complete_pending_pdf":
      return {
        icon: <CheckCircle2 className="h-4 w-4" />,
        label: "Complete (PDF pending)",
        color: "hsl(145 65% 68%)",
        bg: "hsl(145 65% 68% / 0.08)",
        border: "hsl(145 65% 68% / 0.22)",
      };
    case "failed":
    case "pdf_render_failed":
      return {
        icon: <XCircle className="h-4 w-4" />,
        label: status === "pdf_render_failed" ? "PDF render failed" : "Failed",
        color: "hsl(0 100% 75%)",
        bg: "hsl(0 100% 75% / 0.08)",
        border: "hsl(0 100% 75% / 0.22)",
      };
    case "running":
      return {
        icon: <Clock className="h-4 w-4" />,
        label: "Running",
        color: "hsl(217 91% 65%)",
        bg: "hsl(217 91% 65% / 0.08)",
        border: "hsl(217 91% 65% / 0.22)",
      };
    case "awaiting_uploads":
      return {
        icon: <Clock className="h-4 w-4" />,
        label: "Awaiting uploads",
        color: "hsl(41 73% 62%)",
        bg: "hsl(41 73% 62% / 0.08)",
        border: "hsl(41 73% 62% / 0.22)",
      };
    case "processing_uploads":
      return {
        icon: <Clock className="h-4 w-4" />,
        label: "Processing",
        color: "hsl(217 91% 65%)",
        bg: "hsl(217 91% 65% / 0.08)",
        border: "hsl(217 91% 65% / 0.22)",
      };
    default:
      // ready_to_run and any future statuses — display the raw value humanised
      return {
        icon: <Clock className="h-4 w-4" />,
        label: status.replace(/_/g, " "),
        color: "hsl(218 14% 56%)",
        bg: "hsl(218 14% 56% / 0.06)",
        border: "hsl(218 14% 56% / 0.2)",
      };
  }
}

function WUPctPill({ pct }: { pct: number | null }) {
  if (pct == null) return null;
  const atRisk = pct >= 100;
  const isWarn = pct >= 80 && pct < 100;

  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5"
      style={{
        background: atRisk
          ? "hsl(0 100% 75% / 0.1)"
          : isWarn
          ? "hsl(41 73% 62% / 0.1)"
          : "hsl(217 91% 60% / 0.08)",
        color: atRisk
          ? "hsl(0 100% 75%)"
          : isWarn
          ? "hsl(41 73% 68%)"
          : "hsl(217 91% 68%)",
        border: `1px solid ${
          atRisk
            ? "hsl(0 100% 75% / 0.25)"
            : isWarn
            ? "hsl(41 73% 62% / 0.22)"
            : "hsl(217 91% 60% / 0.2)"
        }`,
      }}
    >
      {atRisk && <AlertTriangle className="h-3 w-3" aria-hidden="true" />}
      {pct}% WU
    </span>
  );
}

function AuditRow({ audit }: { audit: Audit }) {
  const cfg = getStatusConfig(audit.status);
  const started = new Date(audit.started_at).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const finished = audit.finished_at
    ? new Date(audit.finished_at).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div
      className="flex items-start gap-4 rounded-xl border px-4 py-3.5 text-sm"
      style={{
        background:
          "linear-gradient(180deg, hsl(223 55% 10% / 0.5), hsl(222 67% 5% / 0.7))",
        borderColor: "hsl(220 18% 85% / 0.1)",
      }}
    >
      {/* Status badge */}
      <div
        className="flex-shrink-0 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium mt-0.5"
        style={{
          background: cfg.bg,
          border: `1px solid ${cfg.border}`,
          color: cfg.color,
          whiteSpace: "nowrap",
        }}
        aria-label={`Status: ${cfg.label}`}
      >
        {cfg.icon}
        {cfg.label}
      </div>

      {/* Details */}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <WUPctPill pct={audit.projected_wu_pct} />
        </div>
        <div
          className="text-xs"
          style={{ color: "hsl(218 14% 50%)", fontFamily: "var(--font-mono, ui-monospace)" }}
        >
          {started}
          {finished && (
            <span style={{ color: "hsl(218 14% 40%)" }}>
              {" "}
              · finished {finished}
            </span>
          )}
        </div>
        {audit.failure_reason && (
          <p
            className="text-xs rounded-md px-2 py-1.5 mt-1"
            style={{
              background: "hsl(0 100% 75% / 0.06)",
              border: "1px solid hsl(0 100% 75% / 0.15)",
              color: "hsl(0 100% 78%)",
            }}
          >
            {audit.failure_reason}
          </p>
        )}
      </div>
    </div>
  );
}

export function AuditListPage() {
  const { appId } = useParams<{ appId: string }>();

  const q = useQuery<Audit[]>({
    queryKey: ["bubble", "audits", appId],
    queryFn: () => fetchAudits(appId!),
    enabled: !!appId,
  });

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Back nav */}
      <div>
        <Link
          to="/apps"
          className="inline-flex items-center gap-1.5 text-sm transition-colors hover:text-foreground"
          style={{ color: "hsl(218 14% 52%)" }}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Apps
        </Link>
      </div>

      <div>
        <h1
          className="text-2xl font-semibold"
          style={{ letterSpacing: "-0.02em" }}
        >
          Audit History
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Recent WU audits for this app — newest first.
        </p>
      </div>

      {q.isLoading && <LoadingState rows={4} />}

      {q.error && (
        <ErrorState
          error={q.error}
          retry={() => q.refetch()}
          description="Could not load audit history for this app."
        />
      )}

      {q.data && q.data.length === 0 && (
        <EmptyState
          icon={<ScanLine className="h-10 w-10 opacity-50" />}
          title="No audits yet"
          description="WU audits run automatically on a schedule once an app is connected. Your first audit result will appear here shortly."
        />
      )}

      {q.data && q.data.length > 0 && (
        <div className="flex flex-col gap-2">
          {q.data.map((a) => (
            <AuditRow key={a.id} audit={a} />
          ))}
        </div>
      )}

      <Footer />
    </div>
  );
}
